import {
    Grid,
    List,
    ListItem,
    ListItemText,
    Theme,
    withStyles,
    WithStyles,
    Typography,
} from '@material-ui/core'
import i18next, { TFunction } from 'i18next'
import { FC, Fragment } from 'react'
import { getFirstCharCapitalized } from '../../../libs/strings'
import { MuiClasses } from '../../../types'
import { formatStringIfDate } from '../../../utils/dates'

const styles = ({ breakpoints }: Theme) => ({
    listItems: {
        padding: '0',
        margin: '0',
        marginBottom: '13px',
        marginTop: '3px',
        color: '#0000008A',
    },
    listItemsTopPart: {
        padding: '0',
        margin: '0',
        color: '#0000008A',
    },
    listItemsTitle: {
        padding: '0',
        paddingTop: '7px',
        margin: '0',
        'text-transform': 'uppercase',
        fontSize: '0.7rem !important',
        '& p': {
            color: '#000000DE',
            fontFamily: 'CircularBold'
        },
    },
    spouseLifeSituation: {
        '& span': {
            fontSize: '1.2rem'
        }
    },
    applicantInfoSubheader: {
        marginTop: '16px',
        marginBottom: '16px',
    },
})

export interface CustomListWithCreditInfoProps extends WithStyles {
    classes: MuiClasses
    TitleTypography?: JSX.Element
    data: any
    applicants: any
    informationOrder: string[]
    translationPath: string
    t: i18next.TFunction
    multiSelectPaths?: { [key: string]: string }
    hideListItemLogic?: (key: string, value: string) => boolean
    capitalizeFirstLetter?: boolean
}

interface ListItemProps {
    t: TFunction
    objectValue: string
    capitalizeFirstLetter?: boolean
}

interface MultiSelectTranslationProps extends ListItemProps {
    multiSelectPath: string
}

const getTranslatedMultiValue = ({
    t,
    multiSelectPath,
    objectValue,
    capitalizeFirstLetter
}: MultiSelectTranslationProps) => {
    const translatedValue = t(`${multiSelectPath}.${objectValue}`)
    return capitalizeFirstLetter
        ? getFirstCharCapitalized(translatedValue)
        : translatedValue
}

const getCapitalized = ({
    t,
    objectValue,
    capitalizeFirstLetter
}: ListItemProps) => {
    const translatedValue = t(`${formatStringIfDate(objectValue)}`)
    return capitalizeFirstLetter
        ? getFirstCharCapitalized(translatedValue)
        : translatedValue
}

const CustomListWithCreditInfo: FC<CustomListWithCreditInfoProps> = ({
    classes,
    t,
    TitleTypography,
    data,
    applicants,
    informationOrder,
    translationPath,
    multiSelectPaths,
    hideListItemLogic,
    capitalizeFirstLetter
}) => {
    const renderSwitch = ({
        key,
        objectValue,
        multiSelectPath,
        isErrorOrNoAnswer
    }: any) => {
        switch (key) {
            case 'phoneNumber':
                return (
                    <Grid item xs={12} md={6} lg={6}>
                        <a
                            style={{
                                textDecoration: 'none',
                                fontFamily: 'CircularRegular'
                            }}
                            href={`tel:+${objectValue}`}
                        >
                            +{objectValue}
                        </a>
                    </Grid>
                )
            case 'pets':
                return (
                    <Grid item xs={12} md={6} lg={6}>
                        <ListItemText
                            primary={getCapitalized({
                                t,
                                objectValue,
                                capitalizeFirstLetter
                            })}
                            className={classes.listItems}
                        />
                    </Grid>
                )
            default:
                return (
                    <Grid item xs={12} md={6} lg={6}>
                        {multiSelectPath && !isErrorOrNoAnswer ? (
                            <>
                                <ListItemText
                                    primary={getTranslatedMultiValue({
                                        t,
                                        objectValue,
                                        capitalizeFirstLetter,
                                        multiSelectPath
                                    })}
                                    className={classes.listItems}
                                />
                            </>
                        ) : (
                            <>
                                <ListItemText
                                    primary={getCapitalized({
                                        t,
                                        objectValue,
                                        capitalizeFirstLetter
                                    })}
                                    className={classes.listItems}
                                />
                            </>
                        )}
                    </Grid>
                )
        }
    }
    return (
        <Fragment>
            {/* POISTA TÄMÄ AIKANAAN */}
            {/* <pre>{JSON.stringify(data, null, 2)}</pre> */}
            {/* <pre>{JSON.stringify(applicants, null, 2)}</pre> */}
            {TitleTypography}

            <Typography
                variant='h5'
                className={classes.applicantInfoSubheader}
            >{t('applicantCard.generalInformation')}</Typography>

            <List
                component={'div' as any}
                disablePadding
                className={classes.innerList}
            >
                {informationOrder.map((key: string, index: number) => {
                    const isErrorOrNoAnswer = hideListItemLogic
                        ? data[key] === 'error' || hideListItemLogic(key, data[key])
                        : data[key] === 'error'
                    let objectValue = isErrorOrNoAnswer ? undefined : data[key]
                    const multiSelectPath = multiSelectPaths && multiSelectPaths[key]
                    let newPath = ''
                    if (
                        (key === 'livelihood' || key === 'spouseLivelihood') &&
                        Array.isArray(data[key])
                    ) {
                        newPath = `newApplication.livelihood`
                        objectValue = data[key]
                            .map((value: string) =>
                                value === 'myAnswer'
                                    ? data['livelihoodCustom']
                                    : t(`newApplication.livelihoodOptions.${value}`)
                            )
                            .join(', ')
                    }
                    if (key === 'movingAbout') {
                        newPath = 'application.list.readyForMoveDate'
                    }
                    if (
                        key === 'lifeSituationCustom' ||
                        key === 'spouseLifeSituationCustom' ||
                        key === 'spouseLifeSituation'
                    ) {
                        newPath = `application.list.lifeSituation`
                    }

                    if (key === 'depositPaymentMethodCustom') {
                        newPath = 'application.list.depositPaymentMethod'
                    }

                    if (objectValue === 'nextMonth') {
                        return <></>
                    }

                    if (key === 'spouseCreditStatus') {
                        newPath = 'application.list.creditStatus'
                    }
                    return (
                        objectValue && (
                            <>
                                {key === 'spouseLifeSituation' && (
                                    <ListItemText
                                        primary={t('application.suposeTitle')}
                                        className={classes.spouseLifeSituation}
                                        style={{
                                            margin: '10px 0'
                                        }}
                                    />
                                )}
                                <ListItem key={`${key}+${index}`} className={classes.listItemsTopPart}>
                                    <Grid
                                        container
                                        item
                                        direction='row'
                                        justify='space-between'

                                    >
                                        <Grid item xs={12} md={6} lg={6}>
                                            <ListItemText
                                                secondary={
                                                    newPath !== ''
                                                        ? t(`${newPath}`)
                                                        : t(`${translationPath}${key}`)
                                                }
                                                className={classes.listItemsTitle}
                                            />
                                        </Grid>
                                        {renderSwitch({
                                            key,
                                            objectValue,
                                            multiSelectPath,
                                            isErrorOrNoAnswer
                                        })}
                                    </Grid>
                                </ListItem>
                            </>
                        )
                    )
                })}
            </List>
            {applicants && applicants.length > 0 && (
                <List component={'div' as any} disablePadding className={classes.innerList}>
                    {applicants.map((applicant, applicantIndex) => (
                        <>
                            <div key={applicantIndex}>
                                <hr style={{ border: '1px solid #C3C6D1', borderTop: 'none', marginTop: '11px', marginBottom: '24px' }} />

                                <Typography variant='h5' className={classes.applicantInfoSubheader}>
                                    {applicants.length > 1
                                        ? `${t('applicantCard.applicant')} ${applicantIndex + 1} ${t('applicantCard.personalInformation')}`
                                        : `${t('applicantCard.personalInformation')}`}
                                </Typography>
                                <ListItem className={classes.listItems}>
                                    <Grid container item direction='row' justify='space-between' style={{ margin: '1px 0' }}>
                                        <Grid item xs={12} md={6} lg={6}>
                                            <ListItemText secondary={t('applicantCard.name')} className={classes.listItemsTitle} />
                                        </Grid>
                                        <Grid item xs={12} md={6} lg={6}>
                                            <ListItemText
                                                primary={applicant.name}
                                                className={classes.listItems}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6} lg={6}>
                                            <ListItemText secondary={t('applicantCard.idNumber')} className={classes.listItemsTitle} />
                                        </Grid>
                                        <Grid item xs={12} md={6} lg={6}>
                                            <ListItemText
                                                primary={applicant.idNumber}
                                                className={classes.listItems}
                                            />
                                        </Grid>
                                        {applicant.phone &&
                                            <>
                                                <Grid item xs={12} md={6} lg={6}>
                                                    <ListItemText secondary={t('applicantCard.phone')} className={classes.listItemsTitle} />
                                                </Grid>
                                                <Grid item xs={12} md={6} lg={6}>
                                                    <ListItemText
                                                        primary={`+${applicant.phone.replace(/^(\+?)/, '')}`}
                                                        className={classes.listItems}
                                                    />
                                                </Grid>
                                            </>
                                        }
                                        {applicant.email &&
                                            <>
                                                <Grid item xs={12} md={6} lg={6}>
                                                    <ListItemText secondary={t('applicantCard.email')} className={classes.listItemsTitle} />
                                                </Grid>
                                                <Grid item xs={12} md={6} lg={6}>
                                                    <ListItemText
                                                        primary={applicant.email}
                                                        className={classes.listItems}
                                                    />
                                                </Grid>
                                            </>
                                        }
                                        {/* <Grid item xs={12} md={6} lg={6}>
                                            <ListItemText secondary={t('applicantCard.status')} className={classes.listItemsTitle} />
                                        </Grid>
                                        <Grid item xs={12} md={6} lg={6}>
                                            <ListItemText
                                                primary={applicant.status}
                                                className={classes.listItems}
                                            />
                                        </Grid> */}
                                        <Grid item xs={12} md={6} lg={6}>
                                            <ListItemText secondary={t('applicantCard.creditStatus')} className={classes.listItemsTitle} />
                                        </Grid>
                                        <Grid item xs={12} md={6} lg={6}>
                                            <ListItemText
                                                primary={t(`applicantCard.${applicant.creditStatus}`)}
                                                className={classes.listItems}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6} lg={6}>
                                            <ListItemText secondary={t('applicantCard.paymentDefaultCount')} className={classes.listItemsTitle} />
                                        </Grid>
                                        <Grid item xs={12} md={6} lg={6}>
                                            <ListItemText
                                                primary={applicant.creditInfo?.paymentDefaultCount}
                                                className={classes.listItems}
                                            />
                                        </Grid>
                                        {applicant.creditInfo && applicant.creditInfo.paymentDefaultCount > 0 && applicant.creditInfo.creditInformationEntryRow && (
                                            <>
                                                {(Array.isArray(applicant.creditInfo.creditInformationEntryRow)
                                                    ? applicant.creditInfo.creditInformationEntryRow
                                                    : [applicant.creditInfo.creditInformationEntryRow]).map((entry: any, index: any) => (
                                                        <>
                                                            <Grid item xs={12}>
                                                                <Typography
                                                                    variant='h6'
                                                                    style={{ marginTop: '15px', fontFamily: 'CircularBold' }}
                                                                >
                                                                    {applicant.creditInfo.creditInformationEntryRow.length > 1
                                                                        ? `${t('applicantCard.creditDefaultInformation')} ${index + 1}`
                                                                        : t('applicantCard.creditDefaultInformation')}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid container key={index} style={{ margin: '5px 0' }}>
                                                                <Grid item xs={12} md={6} lg={6}>
                                                                    <ListItemText secondary={t('applicantCard.creditor')} className={classes.listItemsTitle} />
                                                                </Grid>
                                                                <Grid item xs={12} md={6} lg={6}>
                                                                    <ListItemText primary={entry.creditor} className={classes.listItems} />
                                                                </Grid>
                                                                {entry.amountOfPaymentDefault && (
                                                                    <>
                                                                        <Grid item xs={12} md={6} lg={6}>
                                                                            <ListItemText secondary={t('applicantCard.amountOfPaymentDefault')} className={classes.listItemsTitle} />
                                                                        </Grid>
                                                                        <Grid item xs={12} md={6} lg={6}>
                                                                            <ListItemText primary={entry.amountOfPaymentDefault} className={classes.listItems} />
                                                                        </Grid>
                                                                    </>
                                                                )}
                                                                <Grid item xs={12} md={6} lg={6}>
                                                                    <ListItemText secondary={t('applicantCard.creditInformationEntryText')} className={classes.listItemsTitle} />
                                                                </Grid>
                                                                <Grid item xs={12} md={6} lg={6}>
                                                                    <ListItemText primary={entry.creditInformationEntryText} className={classes.listItems} />
                                                                </Grid>
                                                                <Grid item xs={12} md={6} lg={6}>
                                                                    <ListItemText secondary={t('applicantCard.creditInformationEntryDate')} className={classes.listItemsTitle} />
                                                                </Grid>
                                                                <Grid item xs={12} md={6} lg={6}>
                                                                    <ListItemText primary={entry.creditInformationEntryDate} className={classes.listItems} />
                                                                </Grid>
                                                                <Grid item xs={12} md={6} lg={6}>
                                                                    <ListItemText secondary={t('applicantCard.creditInformationEntryExpires')} className={classes.listItemsTitle} />
                                                                </Grid>
                                                                <Grid item xs={12} md={6} lg={6}>
                                                                    <ListItemText primary={entry.creditInformationEntryExpires} className={classes.listItems} />
                                                                </Grid>
                                                                <Grid item xs={12} md={6} lg={6}>
                                                                    <ListItemText secondary={t('applicantCard.sourceofinformation')} className={classes.listItemsTitle} />
                                                                </Grid>
                                                                <Grid item xs={12} md={6} lg={6}>
                                                                    <ListItemText primary={entry.sourceOfInformation} className={classes.listItems} />
                                                                </Grid>
                                                            </Grid>
                                                        </>
                                                    ))}
                                            </>
                                        )}
                                    </Grid>
                                </ListItem>
                            </div >
                        </>
                    ))}
                </List>
            )
            }
        </Fragment >
    )
}

export default withStyles(styles)(CustomListWithCreditInfo)
